import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getRaces, getLiveLeaderboard } from '../api';
import { getStoredUser } from '../user';
import useIsMobile from '../useIsMobile';

const PODIUM    = ['#FFD700', '#C0C0C0', '#CD7F32'];

// ── Preview mode — visit /live?preview to see dummy data without a live race ──
const IS_PREVIEW = new URLSearchParams(window.location.search).has('preview');
const PREVIEW_RACE = { id: 0, name: 'Japanese Grand Prix', season: 2026, round: 4, race_date: new Date().toISOString(), locked: true };
const PREVIEW_DATA = {
  liveTop10:   ['VER', 'NOR', 'LEC', 'PIA', 'HAM', 'RUS', 'SAI', 'ANT', 'ALO', 'TSU'],
  teamColours: { VER: 'f8c03d', NOR: 'ff8000', LEC: 'e8002d', PIA: 'ff8000', HAM: '27f4d2', RUS: '27f4d2', SAI: 'e8002d', ANT: '27f4d2', ALO: '00897b', TSU: '6692ff' },
  headshots:   {},
  fullNames:   { VER: 'Max Verstappen', NOR: 'Lando Norris', LEC: 'Charles Leclerc', PIA: 'Oscar Piastri', HAM: 'Lewis Hamilton', RUS: 'George Russell', SAI: 'Carlos Sainz', ANT: 'Kimi Antonelli', ALO: 'Fernando Alonso', TSU: 'Yuki Tsunoda' },
  gaps:        { VER: 0, NOR: 4.231, LEC: 8.774, PIA: 12.003, HAM: 15.441, RUS: 19.887, SAI: 24.112, ANT: 28.543, ALO: 33.201, TSU: 38.774 },
  tyres:       { VER: 'MEDIUM', NOR: 'MEDIUM', LEC: 'HARD', PIA: 'SOFT', HAM: 'HARD', RUS: 'MEDIUM', SAI: 'HARD', ANT: 'SOFT', ALO: 'MEDIUM', TSU: 'HARD' },
  tyreLaps:    { VER: 18, NOR: 18, LEC: 31, PIA: 7, HAM: 31, RUS: 18, SAI: 31, ANT: 7, ALO: 18, TSU: 31 },
  raceControl: [{ date: new Date().toISOString(), message: 'SAFETY CAR DEPLOYED', category: 'SafetyCar', flag: null }],
  trackStatus: 'sc',
  leaderboard: [
    { user_name: 'Erik',   score: 43, breakdown: { VER: 25, NOR: 18 } },
    { user_name: 'Maja',   score: 33, breakdown: { VER: 25, LEC: 8 } },
    { user_name: 'Jonas',  score: 25, breakdown: { VER: 25 } },
    { user_name: 'Frida',  score: 18, breakdown: { NOR: 18 } },
    { user_name: 'Viktor', score: 10, breakdown: { HAM: 10 } },
  ],
  isManual: false,
  updatedAt: new Date().toISOString(),
};

const COMPOUNDS = {
  SOFT:         { bg: '#e8002d', fg: '#fff', label: 'S' },
  MEDIUM:       { bg: '#f9b000', fg: '#111', label: 'M' },
  HARD:         { bg: '#e0e0e0', fg: '#111', label: 'H' },
  INTERMEDIATE: { bg: '#22c55e', fg: '#fff', label: 'I' },
  WET:          { bg: '#3b82f6', fg: '#fff', label: 'W' },
};

const keyframes = `
  @keyframes livepulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.35; transform: scale(0.65); }
  }
  @keyframes rowIn {
    from { opacity: 0; transform: translateX(-6px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes redflag {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.75; }
  }
`;

function TyrePill({ compound, laps }) {
  const c = COMPOUNDS[(compound || '').toUpperCase()];
  if (!c) return null;
  if (!laps) {
    return (
      <span style={{
        width: 22, height: 22, borderRadius: '50%',
        background: c.bg, color: c.fg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 11,
        flexShrink: 0,
      }}>{c.label}</span>
    );
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'stretch',
      borderRadius: 5, overflow: 'hidden', flexShrink: 0, height: 22,
      outline: `1px solid ${c.bg}44`,
    }}>
      <span style={{
        width: 22, background: c.bg, color: c.fg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 11,
      }}>{c.label}</span>
      <span style={{ width: 1, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />
      <span style={{
        padding: '0 5px', background: '#131313',
        display: 'inline-flex', alignItems: 'center', gap: 1,
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, color: '#bbb',
      }}>
        {laps}<span style={{ fontSize: 8, color: '#444', marginTop: 1 }}>L</span>
      </span>
    </span>
  );
}

function getBanner(trackStatus) {
  if (!trackStatus || trackStatus === 'clear') return null;
  if (trackStatus === 'red')                   return { bg: '#3b0000', border: '#e8002d', color: '#fca5a5', pulse: true };
  if (trackStatus === 'sc' || trackStatus === 'sc_ending') return { bg: '#2d1d00', border: '#f59e0b', color: '#fcd34d', pulse: false };
  if (trackStatus === 'vsc' || trackStatus === 'vsc_ending') return { bg: '#0a1e3a', border: '#60a5fa', color: '#bfdbfe', pulse: false };
  if (trackStatus === 'chequered')             return { bg: '#1a1a1a', border: '#d4d4d4', color: '#e0e0e0', pulse: false };
  return null;
}

export default function LivePage() {
  const [liveRace, setLiveRace] = useState(null);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  useEffect(() => {
    getRaces()
      .then(races => {
        const live = races.find(r => {
          if (!r.locked || r.official_top10) return false;
          if (!r.race_date) return false;
          const hoursSinceStart = (Date.now() - new Date(r.race_date)) / 36e5;
          return hoursSinceStart < 12;
        });
        setLiveRace(live || null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const fetchLive = useCallback(() => {
    if (!liveRace) return;
    getLiveLeaderboard(liveRace.id)
      .then(d => { setData(d); setLastFetch(new Date()); setError(null); })
      .catch(e => {
        const msg = e.response?.data?.error || e.message || '';
        setError(msg.includes('<') ? 'Live data temporarily unavailable — retrying…' : msg);
      });
  }, [liveRace]);

  useEffect(() => {
    fetchLive();
    const interval = setInterval(fetchLive, 15_000);
    return () => clearInterval(interval);
  }, [fetchLive]);

  if (IS_PREVIEW) {
    return <LiveView race={PREVIEW_RACE} data={PREVIEW_DATA} lastFetch={new Date()} error={null} />;
  }

  if (loading) return <p style={{ color: '#666' }}>Loading...</p>;

  if (!liveRace) {
    return (
      <div style={{ color: '#666', textAlign: 'center', padding: '60px 0' }}>
        <p style={{ fontSize: 20, marginBottom: 8, fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#444' }}>
          No race live right now
        </p>
        <p style={{ fontSize: 13 }}>This page activates once a race is locked by the admin.</p>
      </div>
    );
  }

  return <LiveView race={liveRace} data={data} lastFetch={lastFetch} error={error} />;
}

function LiveView({ race, data, lastFetch, error }) {
  const isMobile      = useIsMobile(640);
  const currentUser   = getStoredUser();
  const latestControl = data?.raceControl?.slice(-1)[0] ?? null;
  const lastScMsg     = data?.raceControl?.slice().reverse().find(m => m.category === 'SafetyCar' || m.category === 'Vsc');
  const scStale       = lastScMsg && (Date.now() - new Date(lastScMsg.date).getTime()) > 3 * 60 * 1000;
  const effectiveStatus = scStale ? 'clear' : data?.trackStatus;
  const banner        = getBanner(effectiveStatus);
  const poolLeaderboard = data?.leaderboard ?? [];
  const maxScore = poolLeaderboard[0]?.score || 1;
  const liveRace = race;

  return (
    <div>
      <style>{keyframes}</style>

      {/* ── HEADER ── */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%', background: '#f9b000', flexShrink: 0,
            animation: 'livepulse 1.2s ease-in-out infinite',
          }} />
          Live · {liveRace.season} Round {liveRace.round}
        </p>
        <h1 style={{
          fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800,
          textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1,
          color: '#f0f0f0', margin: '0 0 6px',
        }}>
          {liveRace.name}
        </h1>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#333', letterSpacing: '0.06em' }}>
          Updates every 15s{lastFetch && ` · Last: ${lastFetch.toLocaleTimeString()}`}
        </p>
      </div>

      {error && (
        <p style={{ color: '#f9b000', fontSize: 12, marginBottom: 16, fontFamily: 'var(--font-mono)', letterSpacing: '0.04em' }}>
          ⚠ {error}
        </p>
      )}

      {/* ── RACE CONTROL BANNER ── */}
      {banner && latestControl && (
        <div style={{
          background: banner.bg,
          border: `1px solid ${banner.border}`,
          borderLeft: `4px solid ${banner.border}`,
          borderRadius: 6,
          padding: '12px 18px',
          marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: 'var(--font-display)',
          textTransform: 'uppercase', letterSpacing: '0.1em',
          fontSize: 15, fontWeight: 800,
          color: banner.color,
          animation: banner.pulse ? 'redflag 1.4s ease-in-out infinite' : 'none',
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: banner.border, flexShrink: 0 }} />
          {latestControl.message}
        </div>
      )}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="live-grid">

          {/* ── LEFT: TIMING TOWER ── */}
          <div style={{ background: '#0e0e0e', borderRadius: 8, overflow: 'hidden', border: '1px solid #181818' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 14px', borderBottom: '1px solid #161616', background: '#0b0b0b',
            }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                {data.isManual ? '⚠ Manual Order' : 'Live Timing'}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f9b000', animation: 'livepulse 1.4s ease-in-out infinite' }} />
                LIVE
              </span>
            </div>

            {data.liveTop10 ? data.liveTop10.map((acr, i) => {
              const headshot   = data.headshots?.[acr];
              const teamColour = data.teamColours?.[acr];
              const rawGap     = data.gaps?.[acr];
              const gap        = rawGap != null
                ? (typeof rawGap === 'number' ? '+' + rawGap.toFixed(3) : String(rawGap).replace(/(\.\d{3})\d+/, '$1'))
                : null;
              const tyre       = data.tyres?.[acr];
              const tyreLaps   = data.tyreLaps?.[acr];
              const tc         = teamColour ? `#${teamColour}` : '#2a2a2a';
              const posColor   = PODIUM[i] ?? '#f9b000';

              const shotSize = isMobile ? 30 : 36;
              return (
                <div key={acr} style={{
                  display: 'flex', alignItems: 'center', gap: isMobile ? 7 : 10,
                  borderBottom: '1px solid #111',
                  background: i < 3 ? 'rgba(255,255,255,0.01)' : 'transparent',
                  animation: 'rowIn 0.3s ease both',
                  animationDelay: `${i * 35}ms`,
                  padding: `0 ${isMobile ? 10 : 14}px 0 0`,
                  minHeight: 46,
                }}>
                  {/* Team stripe */}
                  <div style={{ width: 4, alignSelf: 'stretch', background: tc, flexShrink: 0 }} />

                  {/* Position */}
                  <span style={{
                    fontFamily: 'var(--font-display)', fontSize: isMobile ? 18 : 22, fontWeight: 800,
                    color: posColor, lineHeight: 1,
                    width: isMobile ? 24 : 30, textAlign: 'center', flexShrink: 0, letterSpacing: '-0.01em',
                  }}>
                    {i + 1}
                  </span>

                  {/* Headshot */}
                  {headshot
                    ? <img src={headshot} alt={acr} style={{ width: shotSize, height: shotSize, borderRadius: '50%', objectFit: 'cover', border: `1.5px solid ${tc}`, flexShrink: 0 }} />
                    : <div style={{ width: shotSize, height: shotSize, borderRadius: '50%', background: `${tc}22`, border: `1.5px solid ${tc}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontSize: 9, color: tc }}>{acr}</div>
                  }

                  {/* Acronym */}
                  <span style={{
                    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: isMobile ? 13 : 15,
                    color: '#e0e0e0', letterSpacing: '0.04em', flexShrink: 0,
                  }}>{acr}</span>

                  <span style={{ flex: 1 }} />

                  {/* Tyre pill */}
                  {tyre && <TyrePill compound={tyre} laps={tyreLaps} />}

                  {/* Gap */}
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: isMobile ? 11 : 12, fontWeight: 700,
                    color: i === 0 ? '#f9b000' : '#aaaaaa',
                    minWidth: isMobile ? 54 : 68, textAlign: 'right', flexShrink: 0,
                  }}>
                    {i === 0 ? 'LEADER' : (gap && gap !== '+0.000' ? gap : '—')}
                  </span>
                </div>
              );
            }) : (
              <div style={{ padding: 28, color: '#2a2a2a', fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'center', letterSpacing: '0.1em' }}>
                AWAITING LIVE DATA
              </div>
            )}
          </div>

          {/* ── RIGHT: PREDICTION LEADERBOARD ── */}
          <div style={{ background: '#0e0e0e', borderRadius: 8, overflow: 'hidden', border: '1px solid #181818' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 14px', borderBottom: '1px solid #161616', background: '#0b0b0b',
            }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                Prediction Pool
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#333', letterSpacing: '0.06em' }}>
                {poolLeaderboard.length} ENTRIES
              </span>
            </div>

            {poolLeaderboard.length === 0 && (
              <div style={{ padding: 28, color: '#2a2a2a', fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'center', letterSpacing: '0.1em' }}>
                NO ENTRIES
              </div>
            )}

            {poolLeaderboard.map((row, i) => {
              const isMe     = !!(currentUser && row.user_name === currentUser);
              const isLeader = i === 0;
              const barPct   = maxScore > 0 ? (row.score / maxScore) * 100 : 0;
              const hits     = Object.values(row.breakdown || {}).filter(p => p > 0).length;

              return (
                <div key={row.user_name} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px',
                  borderBottom: '1px solid #111',
                  borderLeft: `3px solid ${isLeader ? 'rgba(249,176,0,0.4)' : isMe ? 'rgba(249,176,0,0.15)' : 'transparent'}`,
                  background: isLeader ? 'rgba(249,176,0,0.04)' : isMe ? 'rgba(249,176,0,0.02)' : 'transparent',
                  animation: 'rowIn 0.35s ease both',
                  animationDelay: `${i * 40}ms`,
                }}>
                  {/* Rank */}
                  <span style={{
                    fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20,
                    color: isLeader ? '#f9b000' : '#222',
                    width: 26, textAlign: 'center', flexShrink: 0, lineHeight: 1,
                  }}>
                    {i + 1}
                  </span>

                  {/* Name + bar */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1,
                      color: isLeader || isMe ? '#fff' : '#777',
                      marginBottom: 5,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {row.user_name}{isMe && ' ★'}
                    </div>
                    <div style={{ height: 2, background: '#181818', borderRadius: 2, overflow: 'hidden', marginBottom: 3 }}>
                      <div style={{
                        height: '100%', borderRadius: 2, width: `${barPct}%`,
                        background: isLeader ? '#f9b000' : isMe ? 'rgba(249,176,0,0.35)' : '#242424',
                        transition: 'width 0.9s ease',
                      }} />
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#2e2e2e' }}>
                      {hits}/10 correct
                    </div>
                  </div>

                  {/* Score */}
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700,
                    color: isLeader ? '#f9b000' : isMe ? 'rgba(249,176,0,0.75)' : '#3a3a3a',
                    flexShrink: 0,
                  }}>
                    {row.score}
                    <span style={{ fontSize: 10, color: '#252525', marginLeft: 2 }}>pts</span>
                  </span>
                </div>
              );
            })}
          </div>

        </div>
      )}

      {!IS_PREVIEW && (
        <div style={{
          marginTop: 18, padding: '18px 20px', background: '#0e0e0e',
          border: '1px solid #181818', borderLeft: '4px solid #f9b000', borderRadius: 8,
        }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#e8002d', letterSpacing: '0.16em', margin: '0 0 7px' }}>
            ● DRIVER LIVE
          </p>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, textTransform: 'uppercase', margin: '0 0 5px', color: '#eee' }}>
            Follow a driver live
          </h2>
          <p style={{ color: '#666', fontSize: 12, lineHeight: 1.5, margin: '0 0 13px' }}>
            Speed, gear, throttle, gaps, tyres, lap data and race-control updates for any driver in the field.
          </p>
          <Link to={`/races/${liveRace.id}/driver-live`} style={{
            display: 'inline-block', padding: '9px 14px', background: '#f9b000', color: '#111', borderRadius: 4,
            textDecoration: 'none', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 13,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Open Driver Live →
          </Link>
        </div>
      )}
    </div>
  );
}
